/**
 * QL-21 — Compliance review lists ("Clients review" / "Flows review") and the
 * PEP/Sanction screening log behind them.
 */

import { addMonthsUtc } from "../calendar-math";
import { parseDbTimestamp } from "../db-timestamp";
import { zonedDateParts, zonedMidnightToUtc } from "../zoned-dates";
import type { ComplianceCheck } from "./compliance-check";
import type { IdentityVerificationSummary } from "./identity-verification";
import type { MatchedIndividual, TrapetsStatus } from "./trapets";

// QL-691 moved `parseDbTimestamp` to `../db-timestamp` (import it from
// `@repo/shared`). These two re-exports keep the old paths compiling for the
// open timezone PRs written against them (#630, #631, #637); drop them, and
// this comment, once those have merged.
export { parseDbTimestamp };

/** Which producer recorded a screening. */
export type ScreeningSource = "batch" | "flow" | "manual";

/**
 * Did the screening run. `failed` records an attempt that could not complete
 * (missing SSN, Trapets error) — distinct from never having been tried, which
 * is the absence of any row at all.
 */
export type ScreeningOutcome = "checked" | "failed";

/**
 * Who the PEP question was actually put to.
 *
 *   self            a Person screened against their own SSN
 *   representative  a natural person screened on behalf of a Company/Group —
 *                   the registered `client_party` representative, or a Group's
 *                   representative member (QP-184 `getActingRepresentatives`)
 *   none            a Company/Group with no representative we could identify,
 *                   so the entity was screened for sanctions and NOBODY was
 *                   screened for PEP
 */
export type ScreeningSubjectKind = "self" | "representative" | "none";

/**
 * Resolve a raw `client.type` for the SCREENING path.
 *
 * Keeps "Group" distinct, unlike `suitabilityClientType`, which folds it into
 * "Person". That fold is right for financial models and wrong here: a Group has
 * no national id of its own, so treating it as a Person made the advisor type a
 * natural person's national id into the group's own SSN field and screened that
 * person under the group's name.
 */
export function screeningClientType(type?: string | null): "Person" | "Company" | "Group" {
  if (type === "Company") return "Company";
  if (type === "Group") return "Group";
  return "Person";
}

/** The natural person a screening's PEP finding is about. */
export type ScreeningSubject = {
  kind: ScreeningSubjectKind;
  /** Null when `kind` is "none". */
  name: string | null;
  ssn: string | null;
  /**
   * QL-c — the subject's date of birth, which for a client with no national id
   * IS the identifier they were screened by. Part of the screening basis for
   * the same reason `ssn` is: swapping one identifier for another means a
   * different search was run, and a verdict given against the old one must not
   * carry over. Optional so screenings recorded before QL-c stay readable.
   */
  dob?: string | null;
};

/**
 * What a screening found. PEP and sanction are INDEPENDENT — Trapets reports
 * them as two booleans and a client can be both, so they are never collapsed
 * into one value.
 *
 * QL-21 follow-up — `isPep` is TRI-STATE, and null is the important case.
 * PEP is a property of a natural person: Trapets has no PEP concept for a
 * company (docs/TRAPETS.MD: "No PEP for companies"). Screening a Company by
 * org number therefore answers the sanction question and leaves the PEP
 * question UNASKED, which is not the same as answering "no".
 *
 * The first cut stored that as `false`, which `resolveScreeningStatus` read as
 * "clear" — so every company passed the compliance gate on a question nobody
 * had put to anyone. null now means "not assessed" and resolves to
 * "incomplete", which gates.
 */
export type ScreeningFindings = {
  /** null = the PEP question was never put to a natural person. */
  isPep: boolean | null;
  isSanctioned: boolean;
};

/**
 * QL-21 — a compliance user's verdict on a client's findings.
 *
 * Deliberately a SEPARATE axis from the findings themselves. A screening says
 * what Trapets found; this says what a human decided about it. Recording the
 * decision as another screening result (which the first cut did) makes a
 * judgement compete with machine facts on newest-wins, so re-running the batch
 * the next day silently threw the decision away — and worse, let a real PEP be
 * recorded as though the screening had come back clean.
 *
 * Follows the goal-level compliance decisions (`VolatilityChartDecision`,
 * `EquityShareChartDecision`): the decision carries the hash of the state it
 * was made against and is voided lazily, never deleted. Re-screening that
 * finds the SAME thing leaves the decision standing; findings that change
 * re-open the review.
 */
export type ScreeningDecision = "accepted" | "blocked" | "not_a_match";

/**
 * QL-b — a compliance user's verdict on ONE matched individual.
 *
 *   confirmed  this listing IS our client
 *   dismissed  this listing is a different person who happens to share the
 *              name, birth date and country we searched on
 *
 * Needed because PEP/sanction lists carry no national id for a person
 * (`MatchedIndividual` has no id field, unlike `MatchedEntity.corporateId`):
 * they record people by name + birth date + country. Any search can therefore
 * return several people, and only a human can say which — if any — is the
 * client. Before this, `accepted` was the only way to close such a review, and
 * `accepted` asserts the OPPOSITE fact: "they are a PEP and we take the
 * relationship anyway".
 */
export type MatchDisposition = "confirmed" | "dismissed";

/**
 * The stable key for one matched individual.
 *
 * Trapets returns no id, so the identity IS these four fields. Shared with
 * `screeningBasisHash` on the API side, which fingerprints matches with the
 * same string — if the two ever drifted, a disposition could be attributed to
 * a different match than the one it was made about.
 *
 * Countries are sorted because Trapets makes no ordering guarantee.
 */
export function matchKey(i: MatchedIndividual): string {
  return `${i.name}|${i.birthDate}|${[...(i.countries ?? [])].sort().join(",")}|${i.listType}`;
}

export type ComplianceReview = {
  decision: ScreeningDecision;
  /** Mandatory — a verdict without a reason is not evidence. */
  note: string;
  decidedAt: string;
  decidedById: string | null;
  decidedByName: string | null;
  /** Hash of the findings this verdict was made against. */
  basisHash: string;
  /**
   * QL-b — per-match verdicts, keyed by `matchKey()`. Absent on reviews
   * recorded before QL-b, and absent when there was nothing to adjudicate
   * (a sanctioned entity match, which has a corporateId and is not ambiguous).
   *
   * This is EVIDENCE, not the outcome: the outcome is `decision`, which the
   * list views can read without the match list (they hold only the cached
   * `client.lastScreening*` columns, never `client_screening.result`).
   */
  matchDispositions?: Record<string, MatchDisposition>;
};

/**
 * Findings × review → what the Clients review list actually shows.
 *
 *   not_checked   never successfully screened, and nothing was attempted
 *   failed        attempted, never succeeded — no findings to judge
 *   clear         screened in full, nothing found
 *   incomplete    the entity was screened and came back clean, but the PEP
 *                 question was never put to a natural person — a Company or
 *                 Group with no representative registered. NOT clear: the
 *                 sanction half passed and the PEP half was never asked.
 *   needs_review  a hit with no valid verdict (never reviewed, or the findings
 *                 moved since the verdict was given)
 *   accepted      a hit a compliance user reviewed and accepted — they ARE a
 *                 PEP/sanctioned and the relationship is taken anyway
 *   blocked       a hit a compliance user reviewed and refused
 *   not_a_match   QL-b — a hit a compliance user determined is a DIFFERENT
 *                 person. Deliberately NOT folded into "clear": clear means
 *                 the lists returned nothing, this means they returned
 *                 something and a human ruled it out. Collapsing the two would
 *                 hide that judgement from the review list, and with it the
 *                 evidence that anyone looked.
 */
export type EffectiveScreeningStatus =
  | "not_checked"
  | "failed"
  | "clear"
  | "incomplete"
  | "needs_review"
  | "accepted"
  | "blocked"
  | "not_a_match";

/**
 * The one place findings and verdict are combined. Pure and shared so the API,
 * the flow-approval gate and the UI can never disagree about a client.
 *
 * Mirrors `resolveRiskBoundEffectiveStatus`: the verdict only counts while its
 * basisHash matches the CURRENT findings.
 */
export function resolveScreeningStatus(
  findings: ScreeningFindings | null,
  currentBasisHash: string | null,
  review: ComplianceReview | null,
  everScreened: boolean,
  lastAttemptFailed: boolean,
): EffectiveScreeningStatus {
  if (!everScreened || !findings) return lastAttemptFailed ? "failed" : "not_checked";

  // A real hit outranks everything: a sanctioned entity needs a verdict whether
  // or not anyone screened its representative for PEP.
  const hasHit = findings.isPep === true || findings.isSanctioned;
  if (hasHit) {
    if (review && currentBasisHash && review.basisHash === currentBasisHash) return review.decision;
    return "needs_review";
  }

  // No hit, but the PEP question was never asked of a natural person. A
  // compliance user can still settle it — an accepted verdict against THIS
  // basis stands, which is how "we screened the representative offline" is
  // recorded — but until then it is not clear.
  if (findings.isPep === null) {
    if (review && currentBasisHash && review.basisHash === currentBasisHash) return review.decision;
    return "incomplete";
  }

  return "clear";
}

/**
 * Combine a client's own search with their representative's into one standing.
 *
 * The two halves answer different questions and neither subsumes the other:
 *
 *   entity search (by org number)  → is the COMPANY sanctioned?
 *   person search (by SSN)         → is that PERSON a PEP, or sanctioned?
 *
 * Which search the PEP answer comes from is decided by the SUBJECT, not by the
 * presence of a second result:
 *
 *   self            a Person screened against their own SSN — their own result
 *                   answers both halves.
 *   representative  a Company/Group — PEP comes from the representative's
 *                   person search, because Trapets reports no PEP for a company
 *                   at all (docs/TRAPETS.MD).
 *   none            a Company/Group with nobody registered to ask. PEP is null,
 *                   never false.
 *
 * Sanctions are the UNION across whatever was searched: a clean company
 * represented by a sanctioned person is a hit, and so is the reverse.
 *
 * A representative search that failed leaves `repStatus` null, which degrades
 * the PEP answer to "not assessed" without voiding the sanction answer we did
 * get — an unanswered question gates, a false "no" does not.
 */
export function combineFindings(input: {
  /** The client's own search: entity for Company/Group, person for Person. */
  ownStatus: TrapetsStatus | null | undefined;
  /** The representative's person search, when one ran. */
  repStatus?: TrapetsStatus | null;
  /** Who the PEP finding is about. */
  subjectKind: ScreeningSubjectKind;
}): { outcome: ScreeningOutcome; findings: ScreeningFindings } {
  const { ownStatus, repStatus, subjectKind } = input;

  // The client's own search decides whether the screening ran at all.
  if (!ownStatus) return { outcome: "failed", findings: { isPep: null, isSanctioned: false } };

  const isSanctioned = Boolean(ownStatus.isSanctioned) || Boolean(repStatus?.isSanctioned);

  if (subjectKind === "self") {
    return {
      outcome: "checked",
      findings: { isPep: Boolean(ownStatus.isPEP), isSanctioned },
    };
  }

  const isPep = subjectKind === "representative" && repStatus ? Boolean(repStatus.isPEP) : null;
  return { outcome: "checked", findings: { isPep, isSanctioned } };
}

/**
 * Does this standing require a compliance decision before advice may proceed?
 *
 * Shared so the flow gate, the review list and any future surface cannot drift
 * on the one question that actually stops work:
 *
 *   needs_review  a hit nobody has signed off
 *   blocked       a hit somebody refused
 *   incomplete    screened, but the PEP question was never put to a natural
 *                 person. This is the case the first cut let through: a Company
 *                 with no registered representative resolved to "clear" and
 *                 passed the gate on a question nobody had asked.
 *
 * `accepted` deliberately does NOT gate — that is what accepting means. Neither
 * does `not_a_match` (QL-b): a compliance user has established the listing is
 * somebody else, so there is no finding about THIS client left to review.
 */
export function screeningRequiresCompliance(status: EffectiveScreeningStatus): boolean {
  return status === "needs_review" || status === "blocked" || status === "incomplete";
}

/**
 * QL-b — is this set of per-match verdicts consistent with the decision?
 *
 * `not_a_match` asserts that NOTHING found was our client, so it is only
 * honest when every match on the table was dismissed. Confirming one and then
 * closing the review as "not a match" would record a PEP as a clean client —
 * the precise false-clear this whole module exists to prevent.
 *
 * Returns null when the pair is coherent, otherwise a reason code (never
 * text, so each side words it in its own language). Pure so the service and
 * the dialog's submit button cannot disagree.
 */
export type DispositionRejection =
  | { reason: "no_matches" }
  | { reason: "matches_left"; count: number };

export function dispositionsRejectDecision(
  decision: ScreeningDecision,
  matchKeys: string[],
  dispositions: Record<string, MatchDisposition> | undefined,
): DispositionRejection | null {
  if (decision !== "not_a_match") return null;
  if (matchKeys.length === 0) return { reason: "no_matches" };
  const undecided = matchKeys.filter((k) => dispositions?.[k] !== "dismissed");
  if (undecided.length > 0) return { reason: "matches_left", count: undecided.length };
  return null;
}

/**
 * Does this flow instance need the compliance approval step?
 *
 * The whole gate in one place — the flow's two settings combined with the
 * client's screening standing:
 *
 *   requireComplianceApproval false  →  never ("No" in Settings → Flows → Process)
 *   scope "all"                      →  always ("All flows")
 *   scope "screening_hit"            →  only when the standing needs attention
 *                                       ("PEP / Sanction hits")
 *
 * Under `screening_hit` the deciding question is `screeningRequiresCompliance`,
 * so all three of these route into the step: an unreviewed hit
 * (`needs_review`), a refused client (`blocked`), and — the case Company/Group
 * screening added — a client whose PEP question was never put to a natural
 * person (`incomplete`).
 *
 * `accepted` deliberately does NOT route: a hit a compliance user has signed
 * off should stop pulling every subsequent flow back into the step. Neither do
 * `clear`, `not_checked` or `failed` — there is no finding to review. A flow
 * that must not proceed unscreened enforces that through its own PEP/Sanction
 * component, which blocks submission; this gate is about reviewing findings.
 */
export function requiresComplianceApproval(
  flow: { requireComplianceApproval: boolean; complianceApprovalScope?: string | null },
  status: EffectiveScreeningStatus,
): boolean {
  if (!flow.requireComplianceApproval) return false;
  if (flow.complianceApprovalScope === "screening_hit") {
    return screeningRequiresCompliance(status);
  }
  return true;
}

/** How a client's row is filtered in the Clients review list. */
export type ScreeningFilter =
  | "all"
  | "needs_review"
  | "incomplete"
  | "pep"
  | "sanctioned"
  | "accepted"
  | "blocked"
  | "clear"
  | "failed"
  | "not_checked";

/**
 * Verified ID standing filter for the Clients review list.
 *
 * `verified` is the standing verification's validity as resolved server-side
 * (see `IdentityVerificationSummary`); the windowed variants narrow that to a
 * verification that is also RECENT, which is what a periodic KYC refresh cycle
 * actually asks about.
 */
export type VerifiedIdFilter = "all" | "verified" | "verified_1y" | "verified_3y" | "not_verified";

/** Last-updated window for the Clients review list. Calendar to-date, not rolling. */
export type LastUpdatedFilter =
  | "anytime"
  | "today"
  | "this_week"
  | "this_month"
  | "this_quarter"
  | "this_year";

/**
 * Does this client's identity standing match the Verified ID filter?
 *
 * A `null` summary means nothing was ever recorded, which is `not_verified` —
 * the same bucket as a verification that has since gone stale, because neither
 * is evidence of a current identity.
 *
 * The windowed variants require a `verifiedAt`: a verification with no
 * timestamp cannot be placed in a window, so it counts as `verified` but is
 * excluded from `verified_1y` / `verified_3y` rather than being assumed recent.
 */
export function matchesVerifiedIdFilter(
  identity: IdentityVerificationSummary | null,
  filter: VerifiedIdFilter,
  now: Date = new Date(),
): boolean {
  if (filter === "all") return true;

  const verified = identity?.verified === true;
  if (filter === "not_verified") return !verified;
  if (filter === "verified") return verified;

  if (!verified) return false;
  const verifiedAt = parseDbTimestamp(identity?.verifiedAt);
  if (verifiedAt === null) return false;

  // Calendar years back, day clamped in UTC (QL-692): host-local `setFullYear`
  // overflowed 29 Feb into 1 Mar, dropping a client verified on 28 Feb a day early.
  const cutoff = addMonthsUtc(now, filter === "verified_1y" ? -12 : -36);
  return verifiedAt.getTime() >= cutoff.getTime();
}

/**
 * Start of the calendar period a `LastUpdatedFilter` names, or null for
 * `anytime` (no bound).
 *
 * Calendar to-date, so "this week" means "since Monday" as the relevant
 * clock reads it — the same basis as the dates rendered beside it. The week
 * starts Monday, matching the date picker (`weekStartsOn: 1`).
 *
 * With no `timezone`, calendar boundaries are read from `now` in whatever
 * timezone the calling environment is already in — this is the original,
 * unchanged behavior every existing (client-side, browser) caller relies on.
 * With a `timezone` (an IANA name), boundaries are computed in that zone
 * instead — QL-349's server-side date-period filters always pass one, so a
 * client's saved timezone (or the `Europe/Oslo` default) decides "today",
 * not the API server's own clock.
 */
export function lastUpdatedPeriodStart(
  filter: LastUpdatedFilter,
  now: Date = new Date(),
  timezone?: string,
): Date | null {
  if (filter === "anytime") return null;

  if (timezone) {
    const { year, month, day, mondayOffset } = zonedDateParts(now, timezone);
    switch (filter) {
      case "today":
        return zonedMidnightToUtc(year, month, day, timezone);
      case "this_week":
        return zonedMidnightToUtc(year, month, day - mondayOffset, timezone);
      case "this_month":
        return zonedMidnightToUtc(year, month, 1, timezone);
      case "this_quarter":
        return zonedMidnightToUtc(year, Math.floor(month / 3) * 3, 1, timezone);
      case "this_year":
        return zonedMidnightToUtc(year, 0, 1, timezone);
    }
  }

  const y = now.getFullYear();
  const m = now.getMonth();

  switch (filter) {
    case "today":
      return new Date(y, m, now.getDate());
    case "this_week": {
      // getDay() is 0 for Sunday, so shift into a Monday-first week before
      // subtracting — otherwise Sunday resolves to the week that is starting
      // rather than the one it closes.
      const mondayOffset = (now.getDay() + 6) % 7;
      return new Date(y, m, now.getDate() - mondayOffset);
    }
    case "this_month":
      return new Date(y, m, 1);
    case "this_quarter":
      return new Date(y, Math.floor(m / 3) * 3, 1);
    case "this_year":
      return new Date(y, 0, 1);
  }
}

/** Was this timestamp written within the calendar period the filter names? */
export function matchesLastUpdatedFilter(
  updatedAt: string | null,
  filter: LastUpdatedFilter,
  now: Date = new Date(),
  timezone?: string,
): boolean {
  const start = lastUpdatedPeriodStart(filter, now, timezone);
  if (start === null) return true;
  const at = parseDbTimestamp(updatedAt);
  if (at === null) return false;
  return at.getTime() >= start.getTime();
}

/** One row of the append-only screening log. */
export type ClientScreening = {
  id: string;
  clientId: string;
  source: ScreeningSource;
  outcome: ScreeningOutcome;
  /** null = the PEP question was not put to anyone (see `ScreeningFindings`). */
  isPep: boolean | null;
  isSanctioned: boolean;
  /** Who the PEP finding is about — the whole point of the log in a
   *  Company/Group context, where it is a different person from the client. */
  subject: ScreeningSubject;
  flowInstanceId: string | null;
  isTestMode: boolean;
  /** The client's OWN screening: the person for a Person, the entity (by org
   *  number) for a Company/Group. */
  result: TrapetsStatus | null;
  /** The representative's person screening, when one was run. Kept separate
   *  from `result` so a reviewer can see the two searches that were made
   *  rather than a merged verdict. */
  representativeResult: TrapetsStatus | null;
  note: string | null;
  screenedAt: string;
  createdById: string | null;
  createdByName: string | null;
  createdAt: string;
};

/** POST /internal/compliance/clients/:id/review — a compliance verdict. */
export type RecordReviewRequest = {
  decision: ScreeningDecision;
  /** Mandatory — a verdict without a reason is not evidence. */
  note: string;
  /**
   * QL-b — per-match verdicts keyed by `matchKey()`. Required in practice when
   * `decision` is "not_a_match" (the service refuses unless every current match
   * is dismissed); optional otherwise, where it records which listing the
   * reviewer actually acted on.
   */
  matchDispositions?: Record<string, MatchDisposition>;
};

/** POST /internal/compliance/clients/:id/screening — record one by hand. */
export type RecordManualScreeningRequest = {
  outcome: ScreeningOutcome;
  /** null records "PEP not assessed" — the same state a Company with no
   *  registered representative lands in. */
  isPep: boolean | null;
  isSanctioned: boolean;
  /** Who was screened. Defaults to the client itself when omitted. */
  subject?: ScreeningSubject;
  /** Required: a manual entry without a justification is not evidence. */
  note: string;
};

export type ComplianceClientRow = {
  clientId: string;
  name: string;
  type: "Person" | "Company" | "Group";
  country: string | null;
  ownerName: string | null;
  /** Newest SUCCESSFUL screening's findings, or null when never screened. */
  findings: ScreeningFindings | null;
  /** Findings × verdict, resolved server-side so every surface agrees. */
  status: EffectiveScreeningStatus;
  /** The stored verdict, whether or not it is still valid — the list shows who
   *  decided and when even once the findings have moved on. */
  review: ComplianceReview | null;
  /** True when the verdict no longer matches the current findings. */
  reviewStale: boolean;
  /** True when the most recent ATTEMPT failed, regardless of prior findings. */
  lastAttemptFailed: boolean;
  /** ISO timestamp of the newest successful screening, null when never. */
  lastCheckedAt: string | null;
  /** Who the PEP finding is about. For a Company/Group this is the registered
   *  representative, so the list can show WHOSE hit it is rather than
   *  attributing a natural person's PEP listing to the company. Null when the
   *  client has never been screened. */
  subject: ScreeningSubject | null;
  /**
   * QL-e — the Verified ID standing.
   *
   * Note this is the CLIENT's badge. The "PEP screened" column on the same row
   * names the REPRESENTATIVE for a Company/Group, and their verification is a
   * different fact — `identity.subjectKind` says which person this summary is
   * actually about, so the two icons on a row cannot be confused.
   */
  identity: IdentityVerificationSummary | null;
  /**
   * The client record's own `updatedAt` — any edit to the client moves it.
   *
   * Deliberately NOT `lastCheckedAt`, which is the screening timestamp already
   * shown in its own column: a reviewer filtering on "changed recently" is
   * asking what an advisor touched, not what Trapets last answered.
   */
  updatedAt: string;
};

/**
 * Risk-bound standing for a whole flow, worst-first: `do_not_proceed` beats
 * `accepted` beats `none`, because a review list must surface the portfolio
 * that was refused rather than average it away against its siblings.
 */
export type RiskDeviationStanding = "none" | "accepted" | "do_not_proceed";

/**
 * Server-side period window for the flows list; `all` applies no date bound.
 * The rolling windows count back from now; the calendar ones (QL-357, the
 * dashboard's Month · Quarter · Year switch) run from the start of the current
 * month, quarter or year in the viewer's timezone.
 */
export type CompliancePeriod =
  | "30d"
  | "90d"
  | "12m"
  | "all"
  | "this_month"
  | "this_quarter"
  | "this_year";

export type ComplianceFlowRow = {
  flowInstanceId: string;
  flowInstanceName: string | null;
  flowName: string;
  clientId: string;
  clientName: string;
  /** QL-e — the client's Verified ID standing, for the badge after the name. */
  identity: IdentityVerificationSummary | null;
  advisorId: string | null;
  advisorName: string | null;
  /** Set when the advisor was deleted (QL-365); the UI marks the name. */
  advisorDeletedAt: string | null;
  startedAt: string;
  /** When the advice was actually given — null until the flow completes. */
  completedAt: string | null;
  status: string;
  goalCount: number;
  portfolioCount: number;
  hasCustomPortfolio: boolean;
  /**
   * Worst risk-bound decision across the flow's goals.
   *
   * QL-493 — read from the STORED goal-level decisions: the volatility chart's
   * and the equity share chart's rows on `flow_instance_component_value`
   * (decision JSON in `calculatedData`), one per goal and measure. It only
   * ever sees a deviation somebody has already decided on: a breach with no
   * decision yet is computed live by the charts and not persisted, so it is
   * indistinguishable from "no deviation" here. Instances decided under the
   * pre-QL-493 portfolio-level panel read as "none".
   */
  riskDeviation: RiskDeviationStanding;
  /**
   * QL-375 — the advisor's recorded mandate deviation decision (the `mandate`
   * component's goal-level rows), kept apart from the risk-bound one. Same
   * standing values, worst decision wins; an undecided breach reads "none".
   */
  mandateDeviation: RiskDeviationStanding;
  /**
   * The client's answer to "is sustainability important?" — the `sustainability`
   * component's global gate, NOT `goalPortfolio.sustainabilityDecision` (which
   * is the advisor's decision about a mismatch). Null = never answered.
   */
  sustainabilityImportant: boolean | null;
  /**
   * One-time investment plus recurring annualised, in the flow's currency.
   *
   * Annualised so one number is comparable across a lump sum and a savings
   * plan: an "amount over X" filter that only counted one-time money would read
   * a large monthly plan as zero and let it through every threshold.
   */
  totalAmount: number;
  /**
   * Compliance-check standing. Only meaningful on a COMPLETED
   * flow — see `ComplianceCheck`. Nothing to do with the in-process approval
   * gate that `status: "compliance_review"` refers to.
   */
  complianceCheck: ComplianceCheck;
};
